import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { EnvelopeSimple, Clock, PaperPlaneTilt, CheckCircle, XCircle, Plus, Trash } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getAutoReportConfig, updateAutoReportConfig, sendReportNow, getAutoReportHistory } from '../../services/api';

export function AutoReportsSettings() {
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cfgRes, histRes] = await Promise.all([getAutoReportConfig(), getAutoReportHistory()]);
      setConfig(cfgRes.data);
      setHistory(histRes.data.items || []);
    } catch { /* silent */ }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateAutoReportConfig(config);
      setConfig(res.data);
      toast.success('Configuration sauvegardee');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const res = await sendReportNow();
      if (res.data.success) {
        toast.success(res.data.message);
        fetchData();
      } else {
        toast.error(res.data.error || 'Erreur envoi');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSending(false);
  };

  const addRecipient = () => {
    if (!newEmail.trim() || !newEmail.includes('@')) { toast.error('Email invalide'); return; }
    if (config.recipients?.includes(newEmail.trim())) { toast.error('Email deja ajoute'); return; }
    setConfig({ ...config, recipients: [...(config.recipients || []), newEmail.trim()] });
    setNewEmail('');
  };

  const removeRecipient = (email) => {
    setConfig({ ...config, recipients: (config.recipients || []).filter(r => r !== email) });
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" /></div>;
  if (!config) return null;

  return (
    <div className="space-y-4" data-testid="auto-reports-settings">
      <Card className="border-[#E8E6E1]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <EnvelopeSimple size={16} className="text-[#2D5A4C]" />
            Rapports automatiques par email
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Enable toggle */}
          <div className="flex items-center justify-between" data-testid="auto-reports-toggle">
            <div>
              <p className="text-sm font-medium text-[#1A2421]">Activer les rapports automatiques</p>
              <p className="text-xs text-[#5E6D66]">Envoi periodique du rapport d'impact ESS par email</p>
            </div>
            <Switch checked={config.enabled} onCheckedChange={v => setConfig({ ...config, enabled: v })} />
          </div>

          {/* Frequency */}
          <div>
            <label className="text-xs text-[#5E6D66] mb-1 block font-medium">Frequence</label>
            <Select value={config.frequency || 'monthly'} onValueChange={v => setConfig({ ...config, frequency: v })}>
              <SelectTrigger className="w-48 h-8 text-xs" data-testid="auto-reports-frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Hebdomadaire</SelectItem>
                <SelectItem value="monthly">Mensuel</SelectItem>
                <SelectItem value="quarterly">Trimestriel</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Options */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-xs text-[#5E6D66]">
              <Switch checked={config.include_wallet !== false} onCheckedChange={v => setConfig({ ...config, include_wallet: v })} />
              Inclure credits/wallet
            </label>
            <label className="flex items-center gap-2 text-xs text-[#5E6D66]">
              <Switch checked={config.include_workflows !== false} onCheckedChange={v => setConfig({ ...config, include_workflows: v })} />
              Inclure automatisations
            </label>
          </div>

          {/* Recipients */}
          <div>
            <label className="text-xs text-[#5E6D66] mb-1.5 block font-medium">Destinataires</label>
            <div className="flex gap-2 mb-2">
              <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email@exemple.com"
                className="h-8 text-xs flex-1" onKeyDown={e => e.key === 'Enter' && addRecipient()} data-testid="auto-reports-email-input" />
              <Button variant="outline" size="sm" className="h-8" onClick={addRecipient} data-testid="auto-reports-add-email">
                <Plus size={12} className="mr-1" /> Ajouter
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(config.recipients || []).map((email) => (
                <Badge key={email} className="bg-[#F0EFEB] text-[#5E6D66] text-[10px] pl-2 pr-1 py-1 flex items-center gap-1" data-testid={`recipient-${email}`}>
                  {email}
                  <button onClick={() => removeRecipient(email)} className="hover:text-red-500 p-0.5 rounded">
                    <Trash size={10} />
                  </button>
                </Badge>
              ))}
              {(config.recipients || []).length === 0 && (
                <p className="text-[10px] text-[#5E6D66] italic">Aucun destinataire configure</p>
              )}
            </div>
          </div>

          {/* Next send info */}
          {config.enabled && config.next_send_at && (
            <div className="bg-[#F9F8F5] rounded-lg p-3 flex items-center gap-2 text-xs text-[#5E6D66]">
              <Clock size={14} className="text-[#2D5A4C]" />
              Prochain envoi prevu : {new Date(config.next_send_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" size="sm" data-testid="auto-reports-save-btn">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Sauvegarder
            </Button>
            <Button variant="outline" size="sm" onClick={handleSendNow} disabled={sending} data-testid="auto-reports-send-now-btn">
              {sending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <PaperPlaneTilt size={12} className="mr-1" />}
              Envoyer maintenant
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      {history.length > 0 && (
        <Card className="border-[#E8E6E1]" data-testid="auto-reports-history">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <Clock size={14} className="text-[#5E6D66]" />
              Historique d'envoi ({history.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#F0EFEB]">
              {history.map((h) => (
                <div key={h.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`report-history-${h.id}`}>
                  {h.success
                    ? <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" weight="fill" />
                    : <XCircle size={14} className="text-red-400 flex-shrink-0" weight="fill" />
                  }
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#1A2421]">{h.subject || 'Rapport Impact ESS'}</p>
                    <p className="text-[10px] text-[#5E6D66]">
                      {h.sent_at ? new Date(h.sent_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      {h.recipients && ` — ${h.recipients.join(', ')}`}
                    </p>
                  </div>
                  <Badge className={h.success ? 'bg-emerald-100 text-emerald-700 text-[9px]' : 'bg-red-100 text-red-700 text-[9px]'}>
                    {h.success ? 'Envoye' : 'Echec'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
